import { createSiteAnchor, LatLonAlt, SiteAnchor } from "../../models/SiteAnchor";
import { WorldRenderer } from "../WorldRenderer";
import { BaseService } from "./BaseService";
import { getCreds } from "../../contexts/PageContext";
import { loadOrgRecords, loadRecord, loadRecords, saveModel } from "../../api/connection";
import { PlanetService } from "./PlanetService";
import { SiteAnchorVisual } from "../../visuals/SiteAnchorVisual";
import { loadFont } from "../../api/threed";
import { Font } from "three/examples/jsm/loaders/FontLoader";
import Papa from "papaparse";
import { CityMarkerVisual } from "../../visuals/CityMarkerVisual";
import { WREvents } from "../WREvents";
import { BaseVisual } from "../../visuals/BaseVisual";
import { Visual } from "../../models/Visual";
import { buildVisual } from "../../visuals/builder";
import * as THREE from "three";
import { IAgent } from "../../visuals/ModelBasedVisual";

export class DataService extends BaseService {
  public sites: Map<string, SiteAnchorVisual> = new Map<string, SiteAnchorVisual>();
  public visuals: Map<string, BaseVisual> = new Map<string, BaseVisual>();
  private sortedVisuals: BaseVisual[] = [];
  public activeSite: SiteAnchorVisual | null = null;
  public font!: Font;
  public planet!: PlanetService;
  public cityMarkers: CityMarkerVisual[] = [];
  public scenarioClient?:IAgent;

  constructor(worldRenderer: WorldRenderer) {
    super(worldRenderer);
  }

  public findVisual(id: string): BaseVisual | null {
    for (const visual of this.visuals.values()) {
      if (visual.id === id) {
        return visual;
      }
    }
    return null;
  }

  public findVisualsByType(type: string): BaseVisual[] {
    const results: BaseVisual[] = [];
    for (const visual of this.visuals.values()) {
      if (visual.getType() === type) {
        results.push(visual);
      }
    }
    return results;
  }


  render(delta: number): void {
    for (const visual of this.sortedVisuals) {
      visual.render(delta);
    }
    if (this.scenarioClient) {
      this.scenarioClient.render(delta);
    }
  }

  public async loadVisual(recordOrRecordId: string | Visual) {
    // console.log("Loading visual:", recordOrRecordId);
    let data: Visual;
    if (typeof recordOrRecordId === 'string') {
      data = (await loadRecord<Visual>('visuals', recordOrRecordId))!;
    } else {
      data = recordOrRecordId as Visual;
    }
    if (!data) {
      throw new Error(`Visual with ID ${recordOrRecordId} not found.`);
    }
    const visual = await buildVisual(this.worldRenderer, data);
    this.visuals.set(visual._id, visual);
    this.addToSortedVisuals(visual);
    this.worldRenderer.emitEvent(WREvents.VisualLoaded, visual);
    return visual;
  }

  public async activateSite(id: string, overrideBox = false): Promise<void> {
    this.deactivateSite();
    if (id && this.sites.has(id)) {
      this.activeSite = this.sites.get(id)!;
      this.activeSite.showBox = overrideBox ? true : this.activeSite.showBox;
      this.activeSite.activate();

      this.planet.worldGroup.position.copy(this.activeSite.obj.getWorldPosition(new THREE.Vector3()).clone().negate());
      this.planet.worldGroup.updateMatrixWorld();
      const visuals = await loadRecords<Visual>('visuals', { siteAnchorId: id });
      // console.log("Loaded visuals:", visuals);
      const bvs: BaseVisual[] = [];
      for (const visualData of visuals) {
        bvs.push(await this.loadVisual(visualData));
      }
      // adjust objects to the scene, in case they have dependencies on other objects
      for (const bv of bvs) {
        bv.sceneAdjust();
      }
      const sitePhysX = [this.activeSite, ...bvs];
      this.worldRenderer.physXService.replaceObjects(sitePhysX);
      this.worldRenderer.emitEvent(WREvents.SiteActivated, this.activeSite);
    } else {
      this.activeSite = null;
    }
  }

  public removeVisual(visual: BaseVisual) {
    visual.obj.parent?.remove(visual.obj); // Remove visual from scene
    this.visuals.delete(visual._id); // Remove from visuals map
    visual.removeAllListeners(); // Clean up event listeners
    this.removeFromSortedVisuals(visual);
    this.worldRenderer.emitEvent(WREvents.VisualRemoved, visual);
  }

  public deactivateSite() {
    if (this.activeSite) {
      const visualsToRemove = Array.from(this.visuals.values()).filter(v => v.siteAnchorId === this.activeSite!._id);
      for (const visual of visualsToRemove) {
        this.removeVisual(visual);
      }
      this.activeSite.deactivate();
      this.worldRenderer.emitEvent(WREvents.SiteDeactivated, this.activeSite);
      this.activeSite = null;
      this.worldRenderer.planetService.worldGroup.position.set(0, 0, 0);
      this.worldRenderer.scene.updateMatrixWorld();
    }
    this.worldRenderer.physXService.replaceObjects([]);
    this.worldRenderer.physXService.clearMessageHandlers(); // Clear message handlers
  }

  public getActiveSite(): SiteAnchorVisual {
    if (!this.activeSite) {
      throw new Error("No active site anchor is set.");
    }
    if (!this.activeSite.obj) {
      throw new Error("Active site anchor does not have a visual object associated with it.");
    }
    return this.activeSite;
  }

  public updateCityLabelLayout(): void {
    // Sort cities by population (largest first) for priority-based positioning
    const sortedCities = [...this.cityMarkers].sort((a, b) => b.population - a.population);

    // Update each city's label configuration
    for (const cityMarker of sortedCities) {
      cityMarker.updateLabelConfiguration(this.cityMarkers);
    }
  }

  private addToSortedVisuals(visual: BaseVisual) {
    this.sortedVisuals.push(visual);
    this.sortedVisuals.sort((a, b) => b.renderOrder - a.renderOrder);
  }

  private removeFromSortedVisuals(visual: BaseVisual) {
    const index = this.sortedVisuals.indexOf(visual);
    if (index !== -1) {
      this.sortedVisuals.splice(index, 1);
    }
  }

  public async load(): Promise<void> {
    this.planet = this.getService(PlanetService);
    this.font = await loadFont('/assets/fonts/regular.typeface.json');
    // this.font = await loadFont('https://threejs.org/examples/fonts/helvetiker_regular.typeface.json');
    const sites = await loadOrgRecords<SiteAnchor>("siteanchors", createSiteAnchor);
    // console.log({ sites });
    for (const site of sites) {
      const siteAnchorVisual = new SiteAnchorVisual();
      siteAnchorVisual.read(site);
      await siteAnchorVisual.load(this.worldRenderer);
      this.sites.set(site._id, siteAnchorVisual);
      this.visuals.set(site._id, siteAnchorVisual);
      this.addToSortedVisuals(siteAnchorVisual);
    }
    this.worldRenderer.emitEvent(WREvents.SitesUpdated, this.sites);
    // // Load city markers
    // this.cityMarkers = await this.readCSV<CityMarkerVisual>('/assets/geo/worldcitiestop.csv', (row: any) => {
    //   if (row.population < 500000) return null; // Filter out cities with population less than 100,000
    //   const cityMarker = new CityMarkerVisual();
    //   cityMarker.id = row.city_ascii || row.city || ''; // Use ASCII city name or fallback to city name
    //   cityMarker.location = {
    //     lat: row.lat,
    //     lon: row.lng,
    //     alt: 5000 // Default altitude, can be adjusted later
    //   };
    //   cityMarker.population = row.population;
    //   cityMarker._id = row.id || ''; // Use a unique identifier if available
    //   cityMarker.orgId = row.orgId || ''; // Use organization ID if available
    //   return cityMarker;
    // });
    // // console.log({ cityMarkerSample: this.cityMarkers[0] });
    // for (const cityMarker of this.cityMarkers) {
    //   this.visuals.set(cityMarker._id, cityMarker);
    //   await cityMarker.load(this.worldRenderer);
    // }
  }

  public getSite(id: string): SiteAnchorVisual {
    if (!this.sites.has(id)) {
      throw new Error(`Site anchor with id ${id} not found.`);
    }
    return this.sites.get(id)!;
  }

  public async addSite(id: string, loc: LatLonAlt, mgrs: string): Promise<SiteAnchorVisual> {
    // Logic to create a site anchor
    // console.log("Creating site anchor");
    this.deactivateSite();
    const newSiteAnchor = createSiteAnchor();
    const { org } = getCreds();
    newSiteAnchor.orgId = org._id;
    newSiteAnchor.id = id;
    newSiteAnchor.mgrs = mgrs;
    newSiteAnchor.location = loc; // Default location
    await saveModel(newSiteAnchor);
    const siteAnchorVisual = new SiteAnchorVisual();
    siteAnchorVisual.read(newSiteAnchor);
    siteAnchorVisual.load(this.worldRenderer);
    this.sites.set(newSiteAnchor._id, siteAnchorVisual);
    this.visuals.set(newSiteAnchor._id, siteAnchorVisual);
    this.worldRenderer.emitEvent(WREvents.SitesUpdated, this.sites);
    return siteAnchorVisual;
  }

  public async readCSV<T>(filePath: string, xform: (r: any) => T | null): Promise<T[]> {
    const response = await fetch(filePath);
    if (!response.ok) throw new Error(`Failed to fetch ${filePath}`);
    const csvText = await response.text();
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true, // Use first row as headers
        skipEmptyLines: true,
        dynamicTyping: true, // Convert numbers automatically
        complete: (result) => {
          const results: T[] = [];
          for (const row of result.data) {
            const transformed = xform(row);
            if (transformed) {
              results.push(transformed);
            }
          }
          resolve(results);
        },
        error: (error: any) => reject(error),
      });
    });
  }
}